//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { listCardFactory } from 'common/components/core/card/ListCard';
import MlFontIcon from 'common/components/core/font_icon/MlFontIcon';
import { ProgressBar } from 'common/components/core/progress_bar';

import theme from './ProgressCard.scss';

type InProgressParams = {
    progressValue: number,
    showProgressValue?: boolean,
    theme: Object
};

type ProgressComponentParams = {
    success: boolean,
    fail: boolean,
    theme: Object,
    inProgress: boolean,
    progressValue: number
};

type Props = {
    inProgress: boolean,
    success: boolean,
    fail: boolean,
    successText?: string,
    failText?: string,
    theme: Object,
    className?: string,
    progressValue: number,
    showProgressValue?: boolean
};

const DefaultLeftIcon = ({
    leftIcon,
    leftIconBgColor,
    theme,
    leftIconTextColor
}) => {
    return (
        <span className={theme.leftIconWrap}>
            <MlFontIcon
                className={theme.leftIcon}
                iconClass={leftIcon}
                bgColor={leftIconBgColor}
                color={leftIconTextColor}
            />
        </span>
    );
};

const DefaultSuccess = ({ successText, theme }) => {
    return successText ? (
        <span className={theme.success}>{successText}</span>
    ) : null;
};

const DefaultInProgress = ({
    progressValue,
    showProgressValue,
    theme,
    ...others
}: InProgressParams) => {
    return (
        <div className={theme.progressBarWrap}>
            <ProgressBar
                value={progressValue}
                theme={theme}
                type="linear"
                mode="determinate"
                {...others}
            />
            {showProgressValue ? (
                <span className={theme.progressValue}>
                    {`${progressValue}%`}
                </span>
            ) : null}
        </div>
    );
};
const progressFactory = ({
    Success = DefaultSuccess,
    Fail,
    InProgress = DefaultInProgress
}) => {
    const ProgressComponent = ({
        success,
        fail,
        theme,
        inProgress,
        progressValue,
        showProgressValue,
        ...others
    }: ProgressComponentParams) => {
        return (
            <div className={theme.progress}>
                {inProgress ? (
                    <InProgress
                        {...others}
                        theme={theme}
                        progressValue={progressValue}
                        showProgressValue={showProgressValue}
                    />
                ) : null}

                {!inProgress ? (
                    fail && Fail ? (
                        <Fail
                            theme={theme}
                            success={success}
                            fail={fail}
                            inProgress={inProgress}
                            progressValue={progressValue}
                            {...others}
                        />
                    ) : Success ? (
                        <Success
                            theme={theme}
                            success={success}
                            fail={fail}
                            inProgress={inProgress}
                            progressValue={progressValue}
                            {...others}
                        />
                    ) : null
                ) : null}
            </div>
        );
    };
    return ProgressComponent;
};

const progressCardFactory = ({
    Title,
    Subtitle,
    LeftIcon = DefaultLeftIcon,
    RightIcon,
    Success,
    Fail,
    InProgress
}) => {
    const Progress = progressFactory({ Success, Fail, InProgress });
    const ProgressListCard = listCardFactory({
        Title,
        Subtitle,
        LeftIcon,
        RightIcon,
        ExtraContent: Progress
    });
    class ProgressCard extends React.PureComponent<Props, void> {
        render() {
            const {
                inProgress,
                success,
                fail,
                successText,
                failText,
                theme,
                className,
                progressValue,
                showProgressValue,
                ...others
            } = this.props;
            return (
                <ProgressListCard
                    theme={theme}
                    inProgress={inProgress}
                    progressValue={progressValue}
                    success={success}
                    successText={successText}
                    fail={fail}
                    failText={failText}
                    showProgressValue={showProgressValue}
                    className={classnames(className, theme.progressCard)}
                    {...others}
                />
            );
        }
    }
    return themr('ProgressCard', theme)(ProgressCard);
};

const DefaultProgressCard = progressCardFactory({});

export default DefaultProgressCard;
export { progressCardFactory, DefaultProgressCard as ProgressCard };
